import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PlanUsageBanner from '../components/PlanUsageBanner';
import SupportChatButton from '../components/SupportChatButton';
import { useAuth } from '../context/AuthContext';
import { Calendar, Users, Settings, Wrench, BookOpen, BarChart3, UserCog, DollarSign, Mail, Server, Bell, Send, HelpCircle } from 'lucide-react';

const Dashboard = () => {
  const { user, isSuperAdmin, isCalendarAdmin, isCustomer, runningUser, getEffectiveRole, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for auto-opening support chat
  const [autoOpenSupportChat, setAutoOpenSupportChat] = useState(null);
  
  // In new architecture, runningUser is always the correct display user
  const displayUser = runningUser;
  const effectiveRole = getEffectiveRole();

  // Redirect customers to book appointment page (no dashboard for customers)
  useEffect(() => {
    if (!loading && isCustomer()) {
      navigate('/customer/book-appointment', { replace: true });
    }
  }, [loading, isCustomer, navigate]);

  // Handle openSupportChat state from navigation
  // Use a ref to track the initial state to avoid cascading renders
  const initialSupportChatRef = useRef(location.state?.openSupportChat);
  
  useEffect(() => {
    if (initialSupportChatRef.current) {
      setAutoOpenSupportChat(initialSupportChatRef.current);
      initialSupportChatRef.current = null;
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Super Admin Dashboard - manages everything
  const superAdminFeatures = [
    { icon: <Users />, title: 'Manage Calendar Admins', description: 'Manage all Calendar Admins', link: '/admin/users' },
    { icon: <Calendar />, title: 'Manage Calendars', description: 'View and manage all calendars', link: '/admin/calendars' },
    { icon: <DollarSign />, title: 'Manage Plans', description: 'Configure pricing plans and features', link: '/admin/plans' },
    { icon: <Mail />, title: 'External Services', description: 'Configure Email, SMS & IMAP providers', link: '/admin/external-services' },
    { icon: <Bell />, title: 'Notification Templates', description: 'Configure notification messages', link: '/admin/notification-templates' },
    { icon: <HelpCircle />, title: 'Manage FAQ', description: 'Edit FAQ content for public page', link: '/admin/faq' },
    { icon: <Bell />, title: 'Reminder Scheduler', description: 'View and manage booking reminders', link: '/admin/reminders' }
  ];

  // Calendar Admin Dashboard - manages their own business
  // Check if user is on Free plan (including null/undefined plan_type)
  // Use displayUser (runningUser) for Run As mode support
  const isFreePlan = !displayUser?.plan_type || displayUser?.plan_type === 'free' || displayUser?.plan_type === 'Free';
  
  // Check plan features for conditional card display
  // Use displayUser to get the correct plan_features in Run As mode
  const planFeatures = displayUser?.plan_features || {};
  const hasMarketingFeature = planFeatures.marketing === true;
  const hasAdvancedAnalytics = planFeatures.advanced_analytics === true;
  const hasCustomBranding = planFeatures.custom_branding === true;
  const hasMultipleServers = planFeatures.multiple_servers === true;
  
  const calendarAdminFeatures = [
    { icon: <BarChart3 />, title: 'Display Statistics', description: 'View analytics, revenue, and appointments', link: '/dashboard/statistics', requiresFeature: 'advanced_analytics' },
    { icon: <Users />, title: 'Manage Customers', description: 'View and manage your customers', link: '/admin/customers' },  // Updated to calendar-scoped page (Jan 2026)
    { icon: <Calendar />, title: 'My Calendars', description: 'Create and manage your calendars', link: '/admin/calendars' },
    { icon: <Wrench />, title: 'My Services', description: 'Add and manage your services', link: '/admin/services' },
    { icon: <UserCog />, title: 'Manage Servers', description: 'Add and manage staff/servers', link: '/admin/servers', requiresFeature: 'multiple_servers' },
    { icon: <Send />, title: 'Marketing', description: 'Send promotional emails and SMS', link: '/marketing', requiresFeature: 'marketing' },
    { icon: <Mail />, title: 'Branding & Email', description: 'Customize branding and email settings', link: '/email-settings', requiresFeature: 'custom_branding' },
    { icon: <Settings />, title: 'Manage Account', description: 'Manage your plan and account status', link: '/manage-account' }
  ].filter(feature => {
    // Check feature-based access
    if (feature.requiresFeature) {
      switch (feature.requiresFeature) {
        case 'advanced_analytics':
          return hasAdvancedAnalytics;
        case 'custom_branding':
          return hasCustomBranding;
        case 'multiple_servers':
          return hasMultipleServers;
        case 'marketing':
          return hasMarketingFeature;
        default:
          return true;
      }
    }
    return true;
  });

  // Determine which features to show based on role
  const getFeatures = () => {
    if (isSuperAdmin()) return superAdminFeatures;
    if (isCalendarAdmin()) return calendarAdminFeatures;
    return []; // Customers are redirected, no features needed here
  };

  const features = getFeatures();

  // Show loading state while user data is being fetched or redirecting customers
  if (loading || isCustomer()) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="py-20 bg-gradient-to-br from-white to-amber-50">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header 
        autoOpenChatWith={autoOpenSupportChat} 
        onAutoOpenChatHandled={() => setAutoOpenSupportChat(null)} 
      />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome, {displayUser?.full_name}!</h1>
                <p className="text-gray-600">
                  Role: <span className="font-semibold text-blue-600">
                    {effectiveRole === 'super_admin' ? 'Super Admin' : 
                     effectiveRole === 'calendar_admin' ? 'Calendar Admin' : 
                     'Customer'}
                  </span>
                </p>
                {isCalendarAdmin() && (
                  <p className="text-gray-600 mt-1">
                    Plan: <span className="font-semibold capitalize text-blue-600">
                      {user?.plan_type || 'Free'}
                    </span>
                    {' • '}
                    Status: <span className={`font-semibold capitalize ${
                      (!user?.plan_status || user?.plan_status?.toLowerCase() === 'active') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {user?.plan_status === 'on_hold' ? 'On Hold' : user?.plan_status || 'Active'}
                    </span>
                  </p>
                )}
                <p className="text-sm text-gray-500">{displayUser?.email}</p>
              </div>
            </div>
          </div>

          {/* Plan Usage Warning Banner - Only for Calendar Admins */}
          {isCalendarAdmin() && (
            <PlanUsageBanner onUpgradeClick={() => navigate('/pricing')} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                onClick={() => navigate(feature.link)}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
              >
                <div className="text-yellow-500 mb-4">
                  {React.cloneElement(feature.icon, { className: 'w-12 h-12' })}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      
      {/* Support Chat Button - listens for support chat events */}
      <SupportChatButton />
    </div>
  );
};

export default Dashboard;